import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, InputGroup } from "react-bootstrap";
import { useFormContext, get } from "react-hook-form";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Input from "components/atoms/Input/Input";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import usePermissions from "hooks/usePermissions";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import { resendUserInvite } from "api";
import { toast } from "react-toastify";
import errorToast from "helpers/errorToast";
import useTimezoneSelectOptions from "hooks/useTimezoneSelectOptions";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { useGetSupplierCompanySlugs } from "api/queries/user/useGetSupplierCompanySlugs";
import { languageOptions } from "./options";

const ManageTeamMember = function ({
  selectedEditTeamMember,
  showManageDialog,
  closeEditTeamMemberDialog,
  setShowDeleteMessageDialog,
  setDialogMessage,
  inviteOptions,
  handleSubmit,
  onSubmit,
  isLoading,
}) {
  const { getPaymentLocationLabel, permissions: permissionsData } = usePermissions();
  const intl = useIntl();
  const {
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext();
  watch("selected_locations");
  const [searchText, setSearchText] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const paymentLocations = useSortedPaymentLocations();
  const { company_name, permissions } = permissionsData ? JSON.parse(permissionsData) : {};
  const { admin_settings } = permissions || {};
  const viewOnly = Boolean(!admin_settings?.edit_users);
  const canModifySupplierSlug = Boolean(admin_settings?.modify_user_supplier_company_slug);

  const { data: supplierCompanySlugsData, isFetching: isFetchingSupplierSlugs } = useGetSupplierCompanySlugs({
    enabled: showManageDialog && canModifySupplierSlug,
  });
  const supplierCompanySlugOptions = useMemo(
    () =>
      (supplierCompanySlugsData?.supplier_company_slugs || []).map((slug) => ({
        label: slug,
        value: slug,
      })),
    [supplierCompanySlugsData],
  );

  const filteredLocation = useMemo(() => {
    if (searchText?.trim()?.length) {
      return paymentLocations?.filter((option) => option.label.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()));
    }
    return paymentLocations;
  }, [searchText, paymentLocations]);

  const timezoneOptions = useTimezoneSelectOptions();

  useEffect(() => {
    if (selectedEditTeamMember.selected_locations && Array.isArray(selectedEditTeamMember.selected_locations)) {
      setValue(
        "selected_locations",
        selectedEditTeamMember.selected_locations.map((item) => item.cb_account_number),
      );
    }
    setValue("email", selectedEditTeamMember.email);
    setValue("first_name", selectedEditTeamMember.first_name);
    setValue("last_name", selectedEditTeamMember.last_name);
    setValue("user_role", selectedEditTeamMember.user_role);
    setValue("phone_no", selectedEditTeamMember.phone_no);
    setValue("preferred_timezone", selectedEditTeamMember.preferred_timezone);
    setValue("language", selectedEditTeamMember.language);
    setValue("approval_level", selectedEditTeamMember.approval_level);
    if (canModifySupplierSlug) {
      setValue("supplier_company_slug", selectedEditTeamMember.supplier_company_slug || "");
    }
  }, [selectedEditTeamMember?.selected_locations]);

  const defaultOption = [
    {
      label: intl.formatMessage({
        id: "new-request.organisational-details.pleaseSelect",
        defaultMessage: "Please select",
      }),
      value: "",
    },
  ];

  const getOptions = useCallback((value) => {
    const option =
      value &&
      value.map((item) => ({
        label: item.name || item.title,
        value: item.id,
      }));
    return defaultOption.concat(option);
  }, []);

  const handleLocationSelect = (e, currentOption) => {
    const currentValues = getValues("selected_locations") || [];
    if (e.target.checked) {
      clearErrors("selected_locations");
      setValue("selected_locations", [...currentValues, currentOption.value]);
    } else {
      setValue(
        "selected_locations",
        currentValues.filter((item) => item !== currentOption.value),
      );
    }
  };

  const handleDeactivateAccount = () => {
    setDialogMessage({
      header: "Deactivate user",
      title: `Are you sure that you want to deactivate  ${selectedEditTeamMember.first_name} ${selectedEditTeamMember.last_name} from the ${company_name} account?`,
      warning: `If so, ${selectedEditTeamMember.first_name} will not be able to login anymore with immediate effect.`,
      email: selectedEditTeamMember.email,
      action: "Yes, deactivate user",
      success: `user ${selectedEditTeamMember.first_name} ${selectedEditTeamMember.last_name} has been deactivated successfully`,
    });
    setShowDeleteMessageDialog(true);
    closeEditTeamMemberDialog();
  };

  const handleReactivateAccount = () => {
    setDialogMessage({
      header: "Reactivate user",
      title: `Are you sure that you want to reactivate ${selectedEditTeamMember.first_name} ${selectedEditTeamMember.last_name} from the ${company_name} account?`,
      warning: `If so, ${selectedEditTeamMember.first_name} will be able to login with immediate effect.`,
      email: selectedEditTeamMember.email,
      action: "Yes, reactivate user",
      success: `user ${selectedEditTeamMember.first_name} ${selectedEditTeamMember.last_name} has been reactivated successfully`,
    });
    setShowDeleteMessageDialog(true);
    closeEditTeamMemberDialog();
  };

  const handleResendInvite = () => {
    setIsSendingInvite(true);
    resendUserInvite(selectedEditTeamMember.email)
      .then(() => {
        toast(
          intl.formatMessage({
            id: "settings.team-members.invitationSent",
            defaultMessage: "Invitation sent",
          }),
          { type: "success" },
        );
        closeEditTeamMemberDialog();
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSendingInvite(false));
  };

  const getDialogTitle = () => {
    if (viewOnly) {
      return intl.formatMessage({
        id: "settings.team-members.viewTeamMember",
        defaultMessage: "View team member",
      });
    }
    if (selectedEditTeamMember?.email) {
      return intl.formatMessage({
        id: "settings.team-members.editTeamMember",
        defaultMessage: "Edit team member",
      });
    }
    return intl.formatMessage({
      id: "settings.team-members.addTeamMember",
      defaultMessage: "Add team member",
    });
  };

  return (
    <Dialog show={showManageDialog} title={getDialogTitle()} onHide={closeEditTeamMemberDialog}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {selectedEditTeamMember.last_active ? (
          <Row className="mb-3 mt-3">
            <Col lg={4}>
              <div className="mb-3">
                <FormattedMessage id="settings.team-members.lastActive" defaultMessage="Last active" />
              </div>
            </Col>

            <Col lg={8}>
              <div>{selectedEditTeamMember.last_active}</div>
            </Col>
          </Row>
        ) : null}

        <div className="d-flex pt-3 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.team-members.email" defaultMessage="Email" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <Input
              type="text"
              required
              name="email"
              placeholder={intl.formatMessage({
                id: "settings.team-members.email.placeholder",
                defaultMessage: "Eg: 'john@example.com'",
              })}
              className="rounded-input input-width-md"
              disabled={viewOnly}
            />
          </Col>
        </div>

        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.team-members.firstName" defaultMessage="First name" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <Input
              type="text"
              required
              name="first_name"
              placeholder={intl.formatMessage({ id: "settings.team-members.firstName", defaultMessage: "First name" })}
              className="rounded-input input-width-md"
              disabled={viewOnly}
            />
          </Col>
        </div>
        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.team-members.lastName" defaultMessage="Last name" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <Input
              type="text"
              required
              name="last_name"
              placeholder={intl.formatMessage({ id: "settings.team-members.lastName", defaultMessage: "Last name" })}
              className="rounded-input input-width-md"
              disabled={viewOnly}
            />
          </Col>
        </div>
        <div className="d-flex pt-8 pb-16">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold">
              <FormattedMessage id="settings.team-members.phoneNumber" defaultMessage="Phone number" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <Input
              type="text"
              name="phone_no"
              placeholder={intl.formatMessage({
                id: "settings.team-members.phoneNumber.placeholder",
                defaultMessage: "International phone number",
              })}
              className="rounded-input input-width-md"
              disabled={viewOnly}
            />
          </Col>
        </div>
        <Row className="pb-16">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>
        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.team-members.userRole" defaultMessage="User role" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <div className="w-192-px">
              <Dropdown
                useHookForm
                name="user_role"
                options={getOptions(inviteOptions.available_user_roles)}
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelect",
                  defaultMessage: "Please select",
                })}
                required
                disabled={viewOnly}
              />
            </div>
          </Col>
        </div>
        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold">
              <FormattedMessage id="settings.team-members.timezone" defaultMessage="Timezone" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <div className="w-192-px">
              <Dropdown
                useHookForm
                name="preferred_timezone"
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelectTimeZone",
                  defaultMessage: "Please select timezone",
                })}
                options={timezoneOptions}
                disabled={viewOnly}
              />
            </div>
          </Col>
        </div>
        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.team-members.language" defaultMessage="Language" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <div className="mb-3 w-192-px">
              <Dropdown
                useHookForm
                name="language"
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelect",
                  defaultMessage: "Please select",
                })}
                options={languageOptions}
                required
                disabled={viewOnly}
              />
            </div>
          </Col>
        </div>

        <Row className="pb-12">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>

        <Row className="mb-3 mt-3">
          <Col lg={4}>
            <div className="font-weight-bold required">{getPaymentLocationLabel()}</div>
          </Col>

          <Col lg={8} className="pl-0 pr-0">
            <div className="multiselect-filter">
              <ExpandableSearch
                containerClasses="mb-3 w-192-px"
                alwaysExpanded
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              {filteredLocation?.map((item) => (
                <div className="mb-2" key={item.label}>
                  <InputGroup className="checkbox bg-transparent mb-1">
                    <label htmlFor={item.label} className="d-flex align-items-center cursor-pointer mb-0 w-100">
                      <input
                        id={item.label}
                        disabled={viewOnly}
                        type="checkbox"
                        className="d-none"
                        onChange={(e) => handleLocationSelect(e, item)}
                        checked={!!getValues()?.selected_locations?.includes?.(item.value)}
                      />
                      <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                      <span className="font-b3 mr-auto">{item.label}</span>
                    </label>
                  </InputGroup>
                </div>
              ))}
            </div>
            {get(errors, "selected_locations") && (
              <div className="d-block font-b3 invalid-feedback">
                <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
              </div>
            )}
          </Col>
        </Row>
        <Row className="pb-24">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>
        <div className="d-flex pt-8 pb-8">
          <Col lg={4} className="p-0">
            <div className="font-b1 font-weight-bold">
              <FormattedMessage id="settings.team-members.approvalLevels" defaultMessage="Approval levels" />
            </div>
          </Col>
          <Col lg={8} className="p-0">
            <div className="w-192-px">
              <Dropdown
                useHookForm
                name="approval_level"
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelect",
                  defaultMessage: "Please select",
                })}
                options={getOptions(inviteOptions.available_approval_levels)}
                disabled={viewOnly}
              />
            </div>
          </Col>
        </div>
        {canModifySupplierSlug && (
          <div className="d-flex pt-8 pb-8">
            <Col lg={4} className="p-0">
              <div className="font-b1 font-weight-bold">
                <FormattedMessage id="settings.team-members.supplierCompanyCode" defaultMessage="Supplier Company Code" />
              </div>
            </Col>
            <Col lg={8} className="p-0">
              <div className="w-192-px">
                <SearchSelect
                  name="supplier_company_slug"
                  options={supplierCompanySlugOptions}
                  enableManualValue
                  preselect={false}
                  isLoading={isFetchingSupplierSlugs}
                  disable={viewOnly}
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please select",
                  })}
                />
              </div>
            </Col>
          </div>
        )}
        <Col className="pl-0 pr-0 pt-40 d-flex flex-row align-item-center justify-content-betweenr">
          <Button
            className="rounded-pill mt-3 font-b1 btn-size"
            variant="link"
            label={intl.formatMessage({ id: "general.discard", defaultMessage: "Discard" })}
            onClick={closeEditTeamMemberDialog}
          />
          <div className="pl-4 ml-4" />
          {selectedEditTeamMember.is_active && selectedEditTeamMember.status !== "Pending" && !viewOnly && (
            <Button
              onClick={handleDeactivateAccount}
              label={intl.formatMessage({
                id: "settings.team-members.deactivateAccount",
                defaultMessage: "Deactivate account",
              })}
              variant="danger"
              className="rounded-pill mt-3 font-b1 btn-size"
            />
          )}

          {!selectedEditTeamMember.is_active && selectedEditTeamMember.email && !viewOnly && (
            <Button
              onClick={handleReactivateAccount}
              label={intl.formatMessage({
                id: "settings.team-members.reactivateAccount",
                defaultMessage: "Reactivate account",
              })}
              variant="primary"
              className="rounded-pill mt-3 font-b1 btn-size"
            />
          )}

          {!viewOnly && (
            <div className="d-flex ml-auto">
              {selectedEditTeamMember.status === "Pending" && (
                <Button
                  className="rounded-pill mt-3 mr-3 ml-auto font-b1 btn-size"
                  label={intl.formatMessage({ id: "general.resendInvite", defaultMessage: "Resend invite" })}
                  variant="outline-primary"
                  onClick={handleResendInvite}
                  isLoading={isSendingInvite}
                />
              )}
              <Button
                className="rounded-pill mt-3 ml-auto font-b1 btn-size"
                label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
                type="submit"
                isLoading={isLoading}
              />
            </div>
          )}
        </Col>
      </form>
    </Dialog>
  );
};

ManageTeamMember.propTypes = {
  selectedEditTeamMember: PropTypes.instanceOf(Object),
  showManageDialog: PropTypes.bool.isRequired,
  closeEditTeamMemberDialog: PropTypes.func.isRequired,
  setShowDeleteMessageDialog: PropTypes.func.isRequired,
  setDialogMessage: PropTypes.instanceOf(Object).isRequired,
  inviteOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

ManageTeamMember.defaultProps = {
  selectedEditTeamMember: {},
  inviteOptions: [],
};

export default ManageTeamMember;
